import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import TemplateDefault from "../../components/template-default";
import Button from "../../components/button";
import {FaEraser, FaPlusSquare} from "react-icons/fa";
import InputForm from "../../components/input-form";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import {useNavigate} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import Card from "../../components/card";
import SelectComponent from "../../components/select-component";
import statusCellRenderer from "./components/status-cell-renderer";
import projectService from "../../services/project-service";
import hardwareAttendancesService from "../../services/hardware-attendances-service";
import general from "../../utils/general";
import {ProjectContext} from "../../context/project-context";
import InputCheckbox from "../../components/input-checkbox";

const defaultPrepareSelectFilters = {
    status: [
        {value: "Aberto", label: "Aberto"},
        {value: "Multiway", label: "Pendência - Multiway"},
        {value: "Cliente", label: "Pendência - Cliente"},
        {value: "Terceiros", label: "Pendência - Terceiros"}
    ],
    projects: [],
}

const defaultFilters = {
    status: [
        {value: "Aberto", label: "Aberto"},
        {value: "Multiway", label: "Pendência - Multiway"},
        {value: "Cliente", label: "Pendência - Cliente"},
        {value: "Terceiros", label: "Pendência - Terceiros"}
    ],
    projects: [],
    requester: "",
    order: "",
    searchData: "",
    canRefetch: true
}

let initialFilters = defaultFilters;
let initialPrepareSelectFilters = defaultPrepareSelectFilters;

const HardwareAttendance = () => {
    const filter = localStorage.getItem("hardware_filters");
    if (filter) {
        const filterParsed = JSON.parse(filter);
        initialFilters = { ...defaultFilters, ...filterParsed };
        initialPrepareSelectFilters = { ...defaultPrepareSelectFilters, ...{ status: filterParsed.status, projects: filterParsed.projects}};
    }

    const [filters, setFilters] = useState(initialFilters);
    const [prepareSelectFilters, setPrepareSelectFilters] = useState(initialPrepareSelectFilters);
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false)
    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    const {user} = useContext(ProjectContext);
    const onlySeeAttendances = user.permissions.includes("see_attendances");

    const navigate = useNavigate();

    const {data: projects} = useQuery({
        queryFn: () => projectService.options().then(({data}) => data),
        queryKey: ["projects"]
    });

    const { data: queryData, isFetching: queryLoading} = useQuery({
        queryFn: async () => {
            if (filters.canRefetch) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            const response = await hardwareAttendancesService.list(filters);
            return response.data;
        },
        queryKey: ["hardware-attendances", filters],
        refetchInterval: filters.canRefetch ? 60000 : false,
    });

    useEffect(() => {
        const newFilter = {
            status: filters.status,
            projects: filters.projects,
            requester: filters.requester,
            order: filters.order,
            searchData: filters.searchData,
            canRefetch: filters.canRefetch,
        };
        localStorage.setItem("hardware_filters", JSON.stringify(newFilter));
    }, [filters]);

    useEffect(() => {
        if (queryData) {
            setData(queryData);
        }
    }, [queryData]);

    useEffect(() => {
        setIsLoading(false)
    }, [data]);

    const [columnDefs] = useState([
        { field: "id", headerName: "ID", width: 70 },
        { field: "date_initial", headerName: "Abertura",
            cellRenderer: params => {
                return general.formatDate(params.value)
            }
        },
        { field: "status", headerName: "Status", cellRenderer: statusCellRenderer },
        { field: "project_name", headerName: "Projeto", suppressSizeToFit: true, flex: 1, minWidth: 300 },
        { field: "product", headerName: "Produto (Localização)", suppressSizeToFit: true, flex: 1, minWidth: 300 },
        { field: "requester", headerName: "Solicitante" }
    ]);

    const gridRef = useRef();

    const defaultColDef = useMemo(() => ({resizable: false}), []);

    const onCellClicked = (params) => {

        const { event, data } = params;

        if (event.ctrlKey && event.button === 0) {
            window.open(`/hardware-attendances/form/${data.id}`);
        }else{
            navigate(`/hardware-attendances/form/${data.id}`);
        }
    };

    const handleInput = ({target: {id, value}}) => setFilters(prev => ({...prev, [id]: value}));

    const handleSelect = (id, value) => setFilters(prev => ({...prev, [id]: value}));

    const handlePrepareSelect = (id, value) => setPrepareSelectFilters(prev => ({...prev, [id]: value}));

    const clearFields = () => {
        setFilters(defaultFilters);
        setPrepareSelectFilters(defaultPrepareSelectFilters);
    }

    return (
        <TemplateDefault title={"Atendimentos"} isLoading={isLoading || queryLoading}>
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    {
                        !onlySeeAttendances &&
                        <Button col="col-span-2" onClick={() => navigate("/hardware-attendances/form")}>
                            <FaPlusSquare/> Novo chamado
                        </Button>
                    }

                    <div className="col-span-4 text-gray-900">
                        <SelectComponent
                            instanceId="status"
                            label="Status"
                            options={[
                                {value: "Aberto", label: "Aberto"},
                                {value: "Encerrado", label: "Encerrado"},
                                {value: "Multiway", label: "Pendência - Multiway"},
                                {value: "Cliente", label: "Pendência - Cliente"},
                                {value: "Terceiros", label: "Pendência - Terceiros"},
                                {value: "Realizado", label: "Serviço realizado"}
                            ]}
                            value={prepareSelectFilters.status}
                            onChange={(value) => handlePrepareSelect("status", value)}
                            eventMenuClose={() => {
                                handleSelect("status", prepareSelectFilters.status)
                            }}
                            name="status"
                            isMulti
                        />
                    </div>

                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            instanceId="projects"
                            label="Projeto"
                            options={projects ?? []}
                            value={prepareSelectFilters.projects}
                            onChange={(value) => handlePrepareSelect("projects", value)}
                            eventMenuClose={() => {
                                handleSelect("projects", prepareSelectFilters.projects)
                            }}
                            name="projects"
                            isDisabled={onlySeeAttendances}
                            isMulti
                        />
                    </div>

                    <div className={`flex gap-4 ${onlySeeAttendances ? "col-span-5" : "col-span-3"}`}>
                        <InputForm id="requester" col={"w-full"} label="Solicitante" type="search"
                                   value={filters.requester} onChange={handleInput}/>

                        <InputCheckbox
                            col={"text-center"}
                            id="canRefetch"
                            name="canRefetch"
                            label="Atualizar"
                            onChange={({target: {checked}}) => setFilters(prev => ({
                                ...prev,
                                canRefetch: checked
                            }))}
                            checked={filters.canRefetch}
                        />
                    </div>


                    <InputForm id="order" col="col-span-3" label="Ordem de serviço" type="number" value={filters.order}
                               onChange={handleInput}/>

                    <div className="col-span-9">
                        <div className="flex justify-between">
                            <InputForm id="searchData" label="Pesquisar" col="w-[calc(100%-60px)]" type="search"
                                       value={filters.searchData}
                                       onChange={handleInput}/>
                            <div>
                                <Button onClick={() => clearFields()}>
                                    <FaEraser/>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={"ag-theme-quartz grid mt-4"} style={{height: "calc(100vh - 233px)"}}>
                    <AgGridReact
                        ref={gridRef}
                        rowHeight={32}
                        rowData={data ?? []}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        pagination
                        paginationPageSize={500}
                        paginationPageSizeSelector={[20, 50, 100, 500]}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        onCellClicked={onCellClicked}
                        gridOptions={{localeText: LocalizationAgGrid}}
                    />
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default HardwareAttendance;